import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as PresentIcon,
  Cancel as AbsentIcon,
  Schedule as LateIcon,
  Timelapse as HalfDayIcon,
  People as PeopleIcon,
  AccessTime as SlotIcon,
  Shuffle as GenerateIcon,
} from '@mui/icons-material';
import { useBirdProcurements } from '../hooks/useBirds';
import {
  useAttendanceSlots,
  useCreateAttendanceSlot,
  useUpdateAttendanceSlot,
  useDeleteAttendanceSlot,
  useAttendanceGrid,
  useBulkMarkAttendance,
  useAttendanceSummary,
  useRoster,
  useGenerateRoster,
  AttendanceStatus,
  AttendanceSlot,
  AttendanceGridRow,
} from '../hooks/useAttendance';

// ─── Status helpers ───────────────────────────────────────────────────────────

const STATUS_META: Record<
  AttendanceStatus,
  { label: string; color: string; bg: string; Icon: React.ElementType }
> = {
  present: { label: 'Present', color: '#66bb6a', bg: 'rgba(102,187,106,0.12)', Icon: PresentIcon },
  absent: { label: 'Absent', color: '#ef5350', bg: 'rgba(239,83,80,0.12)', Icon: AbsentIcon },
  late: { label: 'Late', color: '#ffa726', bg: 'rgba(255,167,38,0.12)', Icon: LateIcon },
  half_day: { label: 'Half day', color: '#42a5f5', bg: 'rgba(66,165,245,0.12)', Icon: HalfDayIcon },
};

const STATUSES: AttendanceStatus[] = ['present', 'absent', 'late', 'half_day'];

const nextStatus = (current: AttendanceStatus | null): AttendanceStatus =>
  current ? STATUSES[(STATUSES.indexOf(current) + 1) % STATUSES.length] : 'present';

const StatusChip: React.FC<{ status: AttendanceStatus | null; onClick?: () => void }> = ({
  status,
  onClick,
}) => {
  if (!status) {
    return (
      <Chip
        size="small"
        label="— tap to mark"
        onClick={onClick}
        sx={{
          color: 'rgba(255,255,255,0.25)',
          bgcolor: 'rgba(255,255,255,0.05)',
          cursor: 'pointer',
          fontSize: '0.72rem',
        }}
      />
    );
  }
  const meta = STATUS_META[status];
  return (
    <Chip
      size="small"
      icon={<meta.Icon sx={{ fontSize: '14px !important', color: `${meta.color} !important` }} />}
      label={meta.label}
      onClick={onClick}
      sx={{
        color: meta.color,
        bgcolor: meta.bg,
        fontWeight: 600,
        fontSize: '0.72rem',
        cursor: onClick ? 'pointer' : 'default',
      }}
    />
  );
};

// ─── Slot form dialog ─────────────────────────────────────────────────────────

const SLOT_EMPTY = { name: '', start_time: '', end_time: '', sort_order: 0, is_active: true };

const SlotDialog: React.FC<{
  open: boolean;
  batchId: number | string;
  editing?: AttendanceSlot | null;
  onClose: () => void;
}> = ({ open, batchId, editing, onClose }) => {
  const [form, setForm] = useState({ ...SLOT_EMPTY });
  const createMutation = useCreateAttendanceSlot();
  const updateMutation = useUpdateAttendanceSlot();

  React.useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name,
        start_time: editing.start_time ?? '',
        end_time: editing.end_time ?? '',
        sort_order: editing.sort_order,
        is_active: editing.is_active,
      });
    } else {
      setForm({ ...SLOT_EMPTY });
    }
  }, [editing, open]);

  const busy = createMutation.isPending || updateMutation.isPending;

  const handleSave = async () => {
    const payload = {
      batch_id: Number(batchId),
      name: form.name,
      start_time: form.start_time || null,
      end_time: form.end_time || null,
      sort_order: Number(form.sort_order),
      is_active: form.is_active,
    };
    if (editing) {
      await updateMutation.mutateAsync({ id: editing.id, ...payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: { bgcolor: '#0e1015', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3 },
        },
      }}
    >
      <DialogTitle sx={{ color: '#fff', fontWeight: 700 }}>
        {editing ? 'Edit Slot' : 'New Time Slot'}
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}
      >
        <TextField
          label="Name *"
          value={form.name}
          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          size="small"
          fullWidth
          sx={inputSx}
        />
        <Box display="flex" gap={2}>
          <TextField
            label="Start time"
            type="time"
            value={form.start_time}
            onChange={(e) => setForm((p) => ({ ...p, start_time: e.target.value }))}
            size="small"
            fullWidth
            sx={inputSx}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="End time"
            type="time"
            value={form.end_time}
            onChange={(e) => setForm((p) => ({ ...p, end_time: e.target.value }))}
            size="small"
            fullWidth
            sx={inputSx}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Box>
        <TextField
          label="Display order"
          type="number"
          value={form.sort_order}
          onChange={(e) => setForm((p) => ({ ...p, sort_order: Number(e.target.value) }))}
          size="small"
          fullWidth
          sx={inputSx}
        />
        <FormControlLabel
          control={
            <Switch
              checked={form.is_active}
              onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#ff9800' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ff9800' },
              }}
            />
          }
          label={
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
              Active
            </Typography>
          }
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'rgba(255,255,255,0.4)' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!form.name || busy}
          onClick={handleSave}
          sx={{
            bgcolor: '#ff9800',
            color: '#000',
            fontWeight: 700,
            '&:hover': { bgcolor: '#ffa726' },
          }}
        >
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#111',
    color: '#e0e0e0',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
    '&:hover fieldset': { borderColor: 'rgba(255,152,0,0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#ff9800' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#ff9800' },
};

const AttendancePage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [batchId, setBatchId] = useState<string>('');
  const [selectedSlot, setSelectedSlot] = useState<string>('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [slotDialogOpen, setSlotDialogOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<AttendanceSlot | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Record<number, AttendanceStatus>>({});
  const [saving, setSaving] = useState(false);

  // Summary range
  const [sumFrom, setSumFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [sumTo, setSumTo] = useState(new Date().toISOString().split('T')[0]);

  const { data: batchesData } = useBirdProcurements({ per_page: 100, active_only: 1 });
  const batches = batchesData?.data ?? [];

  const { data: slotsData, isFetching: slotsFetching } = useAttendanceSlots(
    batchId ? { batch_id: Number(batchId) } : undefined
  );
  const slots = slotsData?.data ?? [];

  const { data: grid, isFetching: gridFetching } = useAttendanceGrid(
    selectedSlot || undefined,
    date || undefined
  );

  const { data: summaryData } = useAttendanceSummary(batchId || undefined, sumFrom, sumTo);

  const bulkMark = useBulkMarkAttendance();
  const deleteSlot = useDeleteAttendanceSlot();
  const { data: rosterData, isFetching: rosterFetching } = useRoster(batchId || undefined, date);
  const generateRoster = useGenerateRoster();
  const hasRoster = rosterData?.has_roster ?? false;

  // Merge saved statuses with pending
  const effectiveGrid: AttendanceGridRow[] = useMemo(() => {
    if (!grid?.grid) return [];
    return grid.grid.map((row) => ({
      ...row,
      status:
        pendingChanges[row.employee_id] !== undefined
          ? pendingChanges[row.employee_id]
          : row.status,
    }));
  }, [grid, pendingChanges]);

  const hasPending = Object.keys(pendingChanges).length > 0;

  const toggleStatus = (employeeId: number, currentStatus: AttendanceStatus | null) => {
    setPendingChanges((p) => ({ ...p, [employeeId]: nextStatus(currentStatus) }));
  };

  const handleSave = async () => {
    if (!selectedSlot || !date) return;
    setSaving(true);
    try {
      const records = effectiveGrid
        .filter((r) => r.status !== null)
        .map((r) => ({ employee_id: r.employee_id, status: r.status as AttendanceStatus }));
      await bulkMark.mutateAsync({ slot_id: Number(selectedSlot), date, records });
      setPendingChanges({});
    } finally {
      setSaving(false);
    }
  };

  const markAll = (status: AttendanceStatus) => {
    const changes: Record<number, AttendanceStatus> = {};
    effectiveGrid.forEach((r) => {
      changes[r.employee_id] = status;
    });
    setPendingChanges(changes);
  };

  const presentCount = effectiveGrid.filter((r) => r.status === 'present').length;
  const absentCount = effectiveGrid.filter((r) => r.status === 'absent').length;
  const unmarkedCount = effectiveGrid.filter((r) => !r.status).length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #0d0d14 0%, #0d0d0d 100%)',
          border: '1px solid rgba(255,152,0,0.12)',
          borderRadius: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: 'rgba(255,152,0,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PeopleIcon sx={{ color: '#ff9800', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#fff', lineHeight: 1.2 }}>
              Attendance
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.3 }}>
              Track daily worker attendance per time slot
            </Typography>
          </Box>
        </Box>
        <TextField
          select
          label="Batch"
          value={batchId}
          onChange={(e) => {
            setBatchId(e.target.value);
            setSelectedSlot('');
          }}
          size="small"
          sx={{ minWidth: 220, ...inputSx }}
        >
          {batches.map((b: any) => (
            <MenuItem key={b.id} value={String(b.id)}>
              {b.identifier} — {b.color}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 2.5,
          minHeight: 36,
          '& .MuiTabs-indicator': { bgcolor: '#ff9800' },
          '& .MuiTab-root': {
            color: 'rgba(255,255,255,0.4)',
            minHeight: 36,
            textTransform: 'none',
            '&.Mui-selected': { color: '#ff9800' },
          },
        }}
      >
        <Tab label="Mark Attendance" />
        <Tab label="Time Slots" sx={{ display: 'none' }} />
        <Tab label="Summary" />
      </Tabs>

      {/* ── Mark Attendance ─────────────────────────────────────────────────── */}
      {tab === 0 && (
        <Box>
          {/* Controls */}
          <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
            <TextField
              type="date"
              label="Date"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setPendingChanges({});
              }}
              size="small"
              sx={{ minWidth: 160, ...inputSx }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              select
              label="Time slot"
              value={selectedSlot}
              onChange={(e) => {
                setSelectedSlot(e.target.value);
                setPendingChanges({});
              }}
              size="small"
              disabled={!batchId}
              sx={{ minWidth: 180, ...inputSx }}
            >
              {slots
                .filter((s) => s.is_active)
                .map((s) => {
                  const rostered =
                    rosterData?.data.find((r) => r.slot.id === s.id)?.employees ?? [];
                  return (
                    <MenuItem key={s.id} value={String(s.id)}>
                      {s.name}
                      {s.start_time ? ` (${s.start_time})` : ''}
                      {hasRoster ? ` · ${rostered.length} rostered` : ''}
                    </MenuItem>
                  );
                })}
            </TextField>
            {batchId && (
              <Button
                variant={hasRoster ? 'outlined' : 'contained'}
                size="small"
                startIcon={<GenerateIcon />}
                disabled={generateRoster.isPending || rosterFetching}
                onClick={() => generateRoster.mutate({ batch_id: Number(batchId), date })}
                sx={
                  hasRoster
                    ? {
                        color: '#ff9800',
                        borderColor: 'rgba(255,152,0,0.4)',
                        textTransform: 'none',
                      }
                    : {
                        bgcolor: '#ff9800',
                        color: '#000',
                        fontWeight: 700,
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#ffa726' },
                      }
                }
              >
                {generateRoster.isPending
                  ? 'Generating…'
                  : hasRoster
                    ? 'Regenerate roster'
                    : 'Generate roster'}
              </Button>
            )}
            {selectedSlot && date && effectiveGrid.length > 0 && (
              <Box display="flex" gap={1}>
                <Button
                  size="small"
                  onClick={() => markAll('present')}
                  sx={{
                    color: '#66bb6a',
                    borderColor: 'rgba(102,187,106,0.3)',
                    border: '1px solid',
                  }}
                >
                  All present
                </Button>
                <Button
                  size="small"
                  onClick={() => markAll('absent')}
                  sx={{ color: '#ef5350', borderColor: 'rgba(239,83,80,0.3)', border: '1px solid' }}
                >
                  All absent
                </Button>
              </Box>
            )}
          </Box>

          {/* Roster overview card */}
          {hasRoster && !selectedSlot && (
            <Paper
              elevation={0}
              sx={{
                mb: 2.5,
                bgcolor: '#0e1015',
                border: '1px solid rgba(255,152,0,0.12)',
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid rgba(255,152,0,0.08)' }}>
                <Typography sx={{ color: '#ff9800', fontWeight: 700, fontSize: '0.85rem' }}>
                  Today&apos;s Roster
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                {rosterData?.data.map((rs, i) => (
                  <Box
                    key={rs.slot.id}
                    sx={{
                      flex: '1 1 160px',
                      px: 2,
                      py: 1.25,
                      borderRight:
                        i < rosterData.data.length - 1
                          ? '1px solid rgba(255,255,255,0.04)'
                          : 'none',
                    }}
                  >
                    <Typography
                      sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', mb: 0.5 }}
                    >
                      {rs.slot.name}
                    </Typography>
                    {rs.employees.length === 0 ? (
                      <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.8rem' }}>
                        —
                      </Typography>
                    ) : (
                      rs.employees.map((e) => (
                        <Typography
                          key={e.id}
                          sx={{ color: '#e0e0e0', fontSize: '0.82rem', lineHeight: 1.5 }}
                        >
                          {e.name.split(' ')[0]}
                        </Typography>
                      ))
                    )}
                  </Box>
                ))}
              </Box>
            </Paper>
          )}

          {!batchId && (
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', mt: 4, textAlign: 'center' }}>
              Select a batch to begin
            </Typography>
          )}

          {batchId && !selectedSlot && (
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', mt: 4, textAlign: 'center' }}>
              Select a time slot
            </Typography>
          )}

          {selectedSlot && date && (
            <>
              {gridFetching && <LinearProgress sx={{ mb: 1, height: 2 }} />}

              {/* Stats row */}
              {effectiveGrid.length > 0 && (
                <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                  {[
                    { label: 'Present', count: presentCount, color: '#66bb6a' },
                    { label: 'Absent', count: absentCount, color: '#ef5350' },
                    { label: 'Unmarked', count: unmarkedCount, color: 'rgba(255,255,255,0.3)' },
                  ].map(({ label, count, color }) => (
                    <Paper
                      key={label}
                      elevation={0}
                      sx={{
                        px: 2,
                        py: 1,
                        bgcolor: '#111318',
                        border: '1px solid rgba(255,255,255,0.05)',
                        borderRadius: 2,
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                      }}
                    >
                      <Typography sx={{ color, fontWeight: 800, fontSize: '1.3rem' }}>
                        {count}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                        {label}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* Worker list */}
              <Paper
                elevation={0}
                sx={{
                  bgcolor: '#0e1015',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 3,
                  overflow: 'hidden',
                }}
              >
                {effectiveGrid.length === 0 && !gridFetching && (
                  <Typography sx={{ color: 'rgba(255,255,255,0.3)', p: 4, textAlign: 'center' }}>
                    No workers assigned to this batch
                  </Typography>
                )}
                {effectiveGrid.map((row, i) => (
                  <React.Fragment key={row.employee_id}>
                    {i > 0 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.04)' }} />}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 2.5,
                        py: 1.5,
                        '&:hover': { bgcolor: 'rgba(255,152,0,0.03)' },
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '50%',
                          bgcolor: 'rgba(255,152,0,0.12)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Typography sx={{ color: '#ff9800', fontWeight: 700, fontSize: '0.75rem' }}>
                          {row.name.charAt(0).toUpperCase()}
                        </Typography>
                      </Box>
                      <Box flex={1}>
                        <Typography sx={{ color: '#e0e0e0', fontWeight: 500, fontSize: '0.9rem' }}>
                          {row.name}
                        </Typography>
                        {row.marked_by && !pendingChanges[row.employee_id] && (
                          <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7rem' }}>
                            Marked by {row.marked_by.name}
                          </Typography>
                        )}
                        {pendingChanges[row.employee_id] !== undefined && (
                          <Typography sx={{ color: '#ffa726', fontSize: '0.7rem' }}>
                            Unsaved change
                          </Typography>
                        )}
                      </Box>
                      <StatusChip
                        status={row.status}
                        onClick={() => toggleStatus(row.employee_id, row.status)}
                      />
                    </Box>
                  </React.Fragment>
                ))}
              </Paper>

              {hasPending && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                  <Button
                    onClick={() => setPendingChanges({})}
                    sx={{ color: 'rgba(255,255,255,0.4)' }}
                  >
                    Discard
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                      bgcolor: '#ff9800',
                      color: '#000',
                      fontWeight: 700,
                      '&:hover': { bgcolor: '#ffa726' },
                    }}
                  >
                    {saving
                      ? 'Saving…'
                      : `Save ${Object.keys(pendingChanges).length} change${Object.keys(pendingChanges).length !== 1 ? 's' : ''}`}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* ── Slots ────────────────────────────────────────────────────────────── */}
      {tab === 1 && (
        <Box>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!batchId}
              onClick={() => {
                setEditingSlot(null);
                setSlotDialogOpen(true);
              }}
              sx={{
                bgcolor: '#ff9800',
                color: '#000',
                fontWeight: 700,
                '&:hover': { bgcolor: '#ffa726' },
              }}
            >
              Add Slot
            </Button>
          </Box>

          {!batchId && (
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', mt: 4, textAlign: 'center' }}>
              Select a batch to manage its time slots
            </Typography>
          )}

          {batchId && (
            <Paper
              elevation={0}
              sx={{
                bgcolor: '#0e1015',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              {slotsFetching && <LinearProgress sx={{ height: 2 }} />}
              {slots.length === 0 && !slotsFetching && (
                <Typography sx={{ color: 'rgba(255,255,255,0.3)', p: 4, textAlign: 'center' }}>
                  No slots defined for this batch
                </Typography>
              )}
              {slots.map((slot, i) => (
                <React.Fragment key={slot.id}>
                  {i > 0 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.04)' }} />}
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, py: 1.5, gap: 2 }}>
                    <SlotIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                    <Box flex={1}>
                      <Typography sx={{ color: '#e0e0e0', fontWeight: 500 }}>
                        {slot.name}
                      </Typography>
                      {(slot.start_time || slot.end_time) && (
                        <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem' }}>
                          {slot.start_time ?? '?'} – {slot.end_time ?? '?'}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      size="small"
                      label={slot.is_active ? 'Active' : 'Inactive'}
                      sx={{
                        color: slot.is_active ? '#66bb6a' : 'rgba(255,255,255,0.3)',
                        bgcolor: slot.is_active
                          ? 'rgba(102,187,106,0.1)'
                          : 'rgba(255,255,255,0.05)',
                        fontSize: '0.7rem',
                      }}
                    />
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingSlot(slot);
                          setSlotDialogOpen(true);
                        }}
                        sx={{ color: 'rgba(255,255,255,0.35)', '&:hover': { color: '#ff9800' } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => deleteSlot.mutate(slot.id)}
                        sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: '#ef5350' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </React.Fragment>
              ))}
            </Paper>
          )}
        </Box>
      )}

      {/* ── Summary ──────────────────────────────────────────────────────────── */}
      {tab === 2 && (
        <Box>
          <Box display="flex" gap={2} mb={2.5} flexWrap="wrap" alignItems="center">
            <TextField
              type="date"
              label="From"
              value={sumFrom}
              onChange={(e) => setSumFrom(e.target.value)}
              size="small"
              sx={{ minWidth: 160, ...inputSx }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              type="date"
              label="To"
              value={sumTo}
              onChange={(e) => setSumTo(e.target.value)}
              size="small"
              sx={{ minWidth: 160, ...inputSx }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>

          {!batchId && (
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', mt: 4, textAlign: 'center' }}>
              Select a batch to see summary
            </Typography>
          )}

          {summaryData?.data?.map((slotSummary: any) => (
            <Paper
              key={slotSummary.slot.id}
              elevation={0}
              sx={{
                mb: 2,
                bgcolor: '#0e1015',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  borderBottom: '1px solid rgba(255,152,0,0.1)',
                  bgcolor: 'rgba(255,152,0,0.04)',
                }}
              >
                <Typography sx={{ color: '#ff9800', fontWeight: 700 }}>
                  {slotSummary.slot.name}
                </Typography>
                {slotSummary.slot.start_time && (
                  <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem' }}>
                    {slotSummary.slot.start_time} – {slotSummary.slot.end_time}
                  </Typography>
                )}
              </Box>
              {slotSummary.workers.length === 0 && (
                <Typography sx={{ color: 'rgba(255,255,255,0.3)', p: 2, fontSize: '0.85rem' }}>
                  No records in this period
                </Typography>
              )}
              {slotSummary.workers.map((w: any, i: number) => (
                <React.Fragment key={i}>
                  {i > 0 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.04)' }} />}
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, py: 1.25, gap: 2 }}>
                    <Typography
                      sx={{ color: '#e0e0e0', flex: 1, fontWeight: 500, fontSize: '0.9rem' }}
                    >
                      {w.name}
                    </Typography>
                    <Box display="flex" gap={1.5}>
                      {[
                        { key: 'present', color: '#66bb6a' },
                        { key: 'absent', color: '#ef5350' },
                        { key: 'late', color: '#ffa726' },
                        { key: 'half_day', color: '#42a5f5' },
                      ].map(
                        ({ key, color }) =>
                          w[key] > 0 && (
                            <Tooltip key={key} title={key.replace('_', ' ')}>
                              <Typography
                                sx={{
                                  color,
                                  fontWeight: 700,
                                  fontSize: '0.85rem',
                                  minWidth: 20,
                                  textAlign: 'center',
                                }}
                              >
                                {w[key]}
                              </Typography>
                            </Tooltip>
                          )
                      )}
                      <Typography
                        sx={{
                          color: 'rgba(255,255,255,0.25)',
                          fontSize: '0.75rem',
                          alignSelf: 'center',
                        }}
                      >
                        / {w.total}
                      </Typography>
                    </Box>
                  </Box>
                </React.Fragment>
              ))}
            </Paper>
          ))}
        </Box>
      )}

      <SlotDialog
        open={slotDialogOpen}
        batchId={batchId}
        editing={editingSlot}
        onClose={() => {
          setSlotDialogOpen(false);
          setEditingSlot(null);
        }}
      />
    </Box>
  );
};

export default AttendancePage;
